import { DeviceEutGroup, Examination, Project, Status } from "graphql/types"
import { isEutGroup } from "store/typeGuards"
import { createNewExamination } from "./testSetupGrouping"

/**
 * Automatically groups a device into existing test setups or creates new ones
 * based on examination status and existing groupings
 *
 * @param device - The device to group
 * @param project - The current project
 * @returns Updated device with potentially modified examinations
 */
export const autoGroupDeviceIntoTestSetups = (
  device: DeviceEutGroup,
  project: Project
): DeviceEutGroup => {
  if (!isEutGroup(device) || device.examinations.length === 0) {
    return device
  }

  // Get all other EUT devices in the project
  const otherEutDevices = project.devices.filter(
    (d) => isEutGroup(d) && d.id !== device.id
  ) as DeviceEutGroup[]

  const updatedExaminations: Examination[] = []

  for (const examination of device.examinations) {
    // Find existing test setups for this examination (same name + norm)
    const existingTestSetups = findExistingTestSetups(examination, otherEutDevices)

    if (existingTestSetups.length === 0) {
      // No existing test setups, keep original examination
      updatedExaminations.push(examination)
    } else {
      // Check if any existing test setup has OPEN status
      const openTestSetup = existingTestSetups.find(
        (ts) => ts.examinations[0]?.status === Status.OPEN
      )

      if (openTestSetup) {
        // Add to existing OPEN test setup
        const updatedExamination = {
          ...examination,
          id: openTestSetup.examinations[0].id, // Use existing examination ID
          salesQuote: openTestSetup.examinations[0].salesQuote,
          operatingModes: examination.operatingModes, // Preserve operating modes from original examination
        }
        updatedExaminations.push(updatedExamination)
      } else {
        // All existing test setups are not OPEN, create new examination
        const newExamination = createNewExamination(examination, device)
        updatedExaminations.push(newExamination)
      }
    }
  }

  return {
    ...device,
    examinations: updatedExaminations,
  }
}

/**
 * Finds existing test setups for a given examination
 * @param examination - The examination to find test setups for
 * @param otherDevices - Other devices in the project
 * @returns Array of existing test setups
 */
const findExistingTestSetups = (
  examination: Examination,
  otherDevices: DeviceEutGroup[]
): Array<{ examinations: Examination[]; devices: DeviceEutGroup[] }> => {
  const testSetups: Array<{ examinations: Examination[]; devices: DeviceEutGroup[] }> = []

  otherDevices.forEach((device) => {
    device.examinations.forEach((deviceExam) => {
      // Check if this examination matches (same name and norm)
      if (deviceExam.name === examination.name && deviceExam.norm.id === examination.norm.id) {
        // Find existing test setup or create new one
        let testSetup = testSetups.find((ts) => ts.examinations[0]?.id === deviceExam.id)

        if (!testSetup) {
          testSetup = {
            examinations: [deviceExam],
            devices: [],
          }
          testSetups.push(testSetup)
        }

        // Add device to test setup if not already present
        if (!testSetup.devices.find((d) => d.id === device.id)) {
          testSetup.devices.push(device)
        }
      }
    })
  })

  return testSetups
}

/**
 * Checks if a device can be added to an existing test setup
 * @param examination - The examination to check
 * @param existingTestSetup - The existing test setup
 * @returns True if device can be added, false otherwise
 */
export const canAddDeviceToTestSetup = (
  examination: Examination,
  existingTestSetup: { examinations: Examination[]; devices: DeviceEutGroup[] }
): boolean => {
  // Check if examination matches
  const examinationMatches = existingTestSetup.examinations.some(
    (exam) => exam.name === examination.name && exam.norm.id === examination.norm.id
  )

  if (!examinationMatches) {
    return false
  }

  // Check if test setup is OPEN
  const isOpen = existingTestSetup.examinations[0]?.status === Status.OPEN

  return isOpen
}
